import Loadable, {type LoadingComponentProps} from 'react-loadable';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import type {AbilityState} from '@packages/systems/designer-abilities';
import type {
  InternalPanel,
  OnAction,
  Panel,
  Panels,
  PanelName,
  ResolvedShortcutConfig,
  ResolvedPanel,
  SwitchablePanel,
} from './types';

// these are intentionally left as `undefined` - they're used so that
// panel configs can explicitly express the intention behind the
// lack of a Panel component; previously it was an optional
// value, which made this unclear
export const CycleChildren = () => null;
export const RenderChildren = () => null;
export const None = () => null;

export const shouldForwardToChild = (panel: Panel<any>): boolean =>
  typeof panel.metadata.forwardToChild === 'function'
    ? panel.metadata.forwardToChild()
    : Boolean(panel.metadata.forwardToChild);

export const shouldSkipPanel = (panel: Panel<any>): boolean =>
  panel.Panel === CycleChildren ||
  panel.Panel === RenderChildren ||
  panel.Panel === None ||
  shouldForwardToChild(panel);

export const importLazyPanel = <T>(
  loader: () => Promise<{default: React.ComponentType<T>}>,
  Loading: React.ComponentType<LoadingComponentProps>
) =>
  Loadable({
    loader,
    loading: Loading,
    delay: 800,
  });

export const panelFeatureSwitch = <State = undefined>({
  abilityName,
  featureFlag,
  ifDisabled,
  ifEnabled,
}: Omit<SwitchablePanel<State>, 'isSwitchablePanel'>): SwitchablePanel<State> =>
  ({
    isSwitchablePanel: true,
    abilityName,
    featureFlag,
    ifDisabled,
    ifEnabled,
  }) as SwitchablePanel<State>;

export function resolvePanel(
  panel: Panel<unknown> | SwitchablePanel<unknown>,
  abilityState?: AbilityState
): ResolvedPanel<unknown> | undefined {
  if (panel.isSwitchablePanel) {
    let sourcePanel: Panel<unknown> | SwitchablePanel<unknown> | undefined;
    if (panel.featureFlag) {
      sourcePanel = getFeatureFlagOutsideOfReact(panel.featureFlag)
        ? panel.ifEnabled
        : panel.ifDisabled;
    } else if (panel.abilityName && abilityState) {
      sourcePanel = abilityState[panel.abilityName]
        ? panel.ifEnabled
        : panel.ifDisabled;
    }

    // we need to recursively call resolvePanel here in order to handle
    // nested switchable panel definitions, such as:
    //
    // switchablePanel(
    //   featureFlag: 'flag-a',
    //   ifEnabled: PanelA,
    //   ifDisabled: switchablePanel(
    //     featureFlag: 'flag-b',
    //     ifEnabled: PanelB,
    //     ifDisabled: PanelC,
    //   )
    // )
    const chosenPanel = sourcePanel
      ? resolvePanel(sourcePanel, abilityState)
      : undefined;

    if (chosenPanel === undefined) {
      return undefined;
    }

    if (chosenPanel.children) {
      return {
        ...chosenPanel,
        children: chosenPanel.children.reduce((acc, child) => {
          const resolvedPanel = resolvePanel(child, abilityState);
          if (resolvedPanel) {
            acc.push(resolvedPanel);
          }
          return acc;
        }, [] as ResolvedPanel<unknown>[]),
      };
    }

    return chosenPanel as ResolvedPanel<unknown>;
  }

  return panel as ResolvedPanel<unknown>;
}

export const indexPanels = (
  panels: Array<Panel<any> | SwitchablePanel<any>>,
  abilityState?: AbilityState
): Panels => {
  const all: Record<string, any> = {};
  const onAction: Record<string, any> = {};
  const paths: Record<string, any> = {};
  const shortcuts: Array<ResolvedShortcutConfig> = [];
  const indexPanel = (
    acc: Panels,
    _panel: Panel<any> | SwitchablePanel<any>,
    parentPath: Array<PanelName> | Array<never>
  ) => {
    const panel = resolvePanel(_panel, abilityState);

    if (!panel) {
      return acc;
    }

    const {abilityName, shortcut} = panel.metadata;
    if (shortcut) {
      shortcuts.push({
        ...shortcut,
        restrictions: {
          abilityName,
          ...shortcut.restrictions,
        },
        name: panel.name,
      });
    }

    const path = [...parentPath, panel.name];

    if (panel.lifecycles && panel.lifecycles.onAction) {
      for (const actionType in panel.lifecycles.onAction) {
        const callback = panel.lifecycles.onAction[actionType];
        const onActionCallback = onAction[actionType];
        if (onActionCallback) {
          if (process.env.NODE_ENV !== 'production') {
            // if your code causes this error to happen and it's not
            // clear what you should do, reach out to @joe on slack!
            console.error(
              `Error: panel side-effect collision detected. Action type "${actionType}" is used by multiple panels. Consider consolidating shared side-effect into a global handler.`
            );
          }

          // if multiple panels hook into the same action type, we'll
          // handle it by calling them in sequence and exiting if
          // any of them return an action. this behaviour will
          // depend on the declaration order of the panels,
          // so we should avoid it where possible.
          onAction[actionType] = (...args: Parameters<OnAction>) =>
            onActionCallback(...args) || callback?.(...args);
        } else {
          onAction[actionType] = callback;
        }
      }
    }

    let updatedPanel: ResolvedPanel<unknown> | InternalPanel;

    if (panel.children) {
      const indexedChildren = panel.children.reduce(
        (a, c) => indexPanel(a, c, path),
        {all, onAction, paths, array: [], byName: {}, shortcuts} as Panels
      );

      updatedPanel = {
        ...panel,
        children: indexedChildren.array,
        byName: indexedChildren.byName,
      };
    } else {
      updatedPanel = panel;
    }

    if (paths[panel.name]) {
      console.error(
        `Error: panel name collision detected. Please rename the "${panel.name}" panel.`
      );
    }
    paths[panel.name] = path;
    acc.array.push(updatedPanel);
    acc.byName[panel.name] = updatedPanel as InternalPanel;
    all[panel.name] = updatedPanel;

    return acc;
  };

  return panels.reduce((acc, cur) => indexPanel(acc, cur, []), {
    all,
    array: [],
    byName: {},
    onAction,
    paths,
    shortcuts,
  } as Panels);
};
